import { useState, useCallback, useEffect, useRef } from "react";
let buildVersion = null;
try { buildVersion = require('./version.json'); } catch(_) {}

const PLAYERS = ["James","Louie","JO","Beau","Jack","Bryson","Braxton","Arlo"];
const JERSEY = { James:36, Louie:28, JO:39, Beau:13, Arlo:30, Jack:20, Bryson:21, Braxton:5 };
const POS_COLORS = { Server:"#3b82f6", "Front Row":"#f97316", "Back Row":"#22c55e", Bench:"#ef4444" };
const POS_BG    = { Server:"#eff6ff", "Front Row":"#fff7ed", "Back Row":"#f0fdf4", Bench:"#fef2f2" };
const POS_TEXT  = { Server:"#1e40af", "Front Row":"#9a3412", "Back Row":"#14532d", Bench:"#991b1b" };
const RALLY_NAMES = ["Serve","Free Ball 1","Free Ball 2"];
const SLOT_INFO = [
  { label:"Position 1", short:"P1", posType:"Server"    },
  { label:"Position 2", short:"P2", posType:"Front Row" },
  { label:"Position 3", short:"P3", posType:"Front Row" },
  { label:"Position 4", short:"P4", posType:"Front Row" },
  { label:"Position 5", short:"P5", posType:"Back Row"  },
  { label:"Position 6", short:"P6", posType:"Back Row"  },
];

function initStats() {
  return Object.fromEntries(PLAYERS.map(p=>[p,{ralliesPlayed:0,timesServed:0,onCourt:false,currentPos:null}]));
}

export default function App() {
  const [phase, setPhase]               = useState("setup");
  const [setupTab, setSetupTab]         = useState("players");
  const [eligibility, setEligibility]   = useState(Object.fromEntries(PLAYERS.map(p=>[p,{Server:true,"Front Row":true,"Back Row":true}])));
  const [sittingOut, setSittingOut]     = useState(Object.fromEntries(PLAYERS.map(p=>[p,false])));
  const [maxServes, setMaxServes]       = useState(6);
  const [subPairs, setSubPairs]         = useState([]);
  const [newPairCourt, setNewPairCourt] = useState("");
  const [newPairBench, setNewPairBench] = useState("");
  const [newPairSlots, setNewPairSlots] = useState([3]);
  const [court, setCourt]               = useState(Array(6).fill(null));
  const [bench, setBench]               = useState([]);
  const [stats, setStats]               = useState(initStats());
  const [sets, setSets]                 = useState([{scores:[0,0]}]);
  const [currentSet, setCurrentSet]     = useState(0);
  const [servingTeam, setServingTeam]   = useState(0);
  const [rallyInSeq, setRallyInSeq]     = useState(0);
  const [seqCount, setSeqCount]         = useState(0);
  const [log, setLog]                   = useState([]);
  const [subHistory, setSubHistory]     = useState([]);
  const [activeTab, setActiveTab]       = useState("court");
  const [pendingSubSlots, setPendingSubSlots] = useState([]);
  const [subAlerts, setSubAlerts]       = useState([]);
  const [history, setHistory]           = useState([]);
  const [future, setFuture]             = useState([]);
  const [celebration, setCelebration]   = useState(null);
  const [betweenSetInfo, setBetweenSetInfo] = useState(null);
  const dragSrc  = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [dragActive, setDragActive] = useState(null);

  const activePlayers = PLAYERS.filter(p=>!sittingOut[p]);
  const pairedPlayers = subPairs.flatMap(p=>[p.courtPlayer,p.benchPlayer]);
  const unpaired      = activePlayers.filter(p=>!pairedPlayers.includes(p));

  const toggleElig = (p,pos) => setEligibility(e=>({...e,[p]:{...e[p],[pos]:!e[p][pos]}}));
  const addLog = (msg) => setLog(l=>[`[Set ${currentSet+1}] ${msg}`,...l].slice(0,60));

  const addSubPair = () => {
    if(!newPairCourt||!newPairBench||newPairCourt===newPairBench||newPairSlots.length===0) return;
    if(subPairs.find(sp=>sp.courtPlayer===newPairCourt||sp.benchPlayer===newPairCourt||sp.courtPlayer===newPairBench||sp.benchPlayer===newPairBench)) return;
    setSubPairs(s=>[...s,{id:Date.now(),courtPlayer:newPairCourt,benchPlayer:newPairBench,triggerSlots:[...newPairSlots]}]);
    setNewPairCourt(""); setNewPairBench(""); setNewPairSlots([3]);
  };
  const removeSubPair = (id) => setSubPairs(s=>s.filter(p=>p.id!==id));
  const toggleNewPairSlot = (idx) => setNewPairSlots(s=>s.includes(idx)?s.filter(x=>x!==idx):[...s,idx]);

  const goToCourt = useCallback(()=>{
    const benchFirst = subPairs.map(p=>p.benchPlayer);
    const courtEligible = activePlayers.filter(p=>!benchFirst.includes(p));
    const newCourt = Array(6).fill(null);
    const available = [...courtEligible];
    SLOT_INFO.forEach((slot,i)=>{
      const idx = available.findIndex(p=>eligibility[p][slot.posType]);
      if(idx>=0) newCourt[i]=available.splice(idx,1)[0];
      else if(available.length) newCourt[i]=available.splice(0,1)[0];
    });
    const onCourt = newCourt.filter(Boolean);
    const finalBench = [...benchFirst, ...courtEligible.filter(p=>!onCourt.includes(p))];
    const newStats = initStats();
    newCourt.forEach((p,i)=>{ if(p) newStats[p]={...newStats[p],onCourt:true,currentPos:SLOT_INFO[i].posType}; });
    finalBench.forEach(p=>{ newStats[p]={...newStats[p],onCourt:false,currentPos:null}; });
    setCourt(newCourt); setBench(finalBench); setStats(newStats); setPhase("lineup");
  },[activePlayers,eligibility,subPairs]);

  const startGame = () => { setPhase("game"); addLog("Game started! Set 1 begins."); };

  // drag/drop
  const executeDrop = (type,idx) => {
    const src=dragSrc.current;
    if(!src||(src.type===type&&src.idx===idx)){dragSrc.current=null;setDragOver(null);return;}
    const nc=[...court], nb=[...bench];
    if(src.type==="court"&&type==="court"){ [nc[src.idx],nc[idx]]=[nc[idx],nc[src.idx]]; }
    else if(src.type==="bench"&&type==="court"){ const bp=nb[src.idx],cp=nc[idx]; nc[idx]=bp; nb.splice(src.idx,1); if(cp) nb.push(cp); }
    else if(src.type==="court"&&type==="bench"){ const cp=nc[src.idx],bp=nb[idx]; nc[src.idx]=bp||null; nb.splice(idx,1); if(cp) nb.push(cp); }
    else if(src.type==="bench"&&type==="bench"){ const tmp=nb[src.idx]; nb.splice(src.idx,1); nb.splice(idx,0,tmp); }
    else if(src.type==="court"&&type==="benchDrop"){ const cp=nc[src.idx]; nc[src.idx]=null; if(cp) nb.push(cp); }
    setCourt(nc); setBench(nb); dragSrc.current=null; setDragOver(null); setDragActive(null);
  };

  // Single unified pointer-event drag — works for mouse, touch, and stylus.
  // Handlers live on window so they survive React remounting PlayerCard on state changes.
  // executeDropRef always points to the latest executeDrop so it sees fresh court/bench.
  const executeDropRef = useRef(null);
  executeDropRef.current = executeDrop;
  const handlePointerDown = (e,type,idx) => {
    const key = type==='court' ? `court-${idx}` : `bench-${idx}`;
    dragSrc.current={type,idx,pointerId:e.pointerId};
    setDragActive(key);
    document.body.style.userSelect='none';
    document.body.style.cursor='grabbing';
  };
  useEffect(()=>{
    const cleanup = () => {
      dragSrc.current=null; setDragOver(null); setDragActive(null);
      document.body.style.userSelect=''; document.body.style.cursor='';
    };
    const onMove = (e) => {
      if(!dragSrc.current||dragSrc.current.pointerId!==e.pointerId) return;
      e.preventDefault();
      const el=document.elementFromPoint(e.clientX,e.clientY);
      const target=el?.closest('[data-drop-type]');
      setDragOver(target?target.getAttribute('data-drop-key'):null);
    };
    const onUp = (e) => {
      if(!dragSrc.current||dragSrc.current.pointerId!==e.pointerId) return;
      const el=document.elementFromPoint(e.clientX,e.clientY);
      const target=el?.closest('[data-drop-type]');
      if(target){
        const dropType=target.getAttribute('data-drop-type');
        const dropIdxStr=target.getAttribute('data-drop-idx');
        executeDropRef.current(dropType,dropIdxStr!==null?parseInt(dropIdxStr,10):null);
      } else { cleanup(); }
      document.body.style.userSelect=''; document.body.style.cursor='';
    };
    window.addEventListener('pointermove',onMove);
    window.addEventListener('pointerup',onUp);
    window.addEventListener('pointercancel',cleanup);
    return ()=>{
      window.removeEventListener('pointermove',onMove);
      window.removeEventListener('pointerup',onUp);
      window.removeEventListener('pointercancel',cleanup);
    };
  },[]);

  // clockwise rotation: P1→P2→P3→P4→P5→P6→P1
  // visually right-back → right-front → mid-front → left-front → left-back → mid-back → right-back
  // new[i] = old[(i+1)%6]
  const rotateCourt = (c) => { const n=Array(6).fill(null); for(let i=0;i<6;i++) n[i]=c[(i+1)%6]; return n; };

  const makeSnapshot = () => ({
    court:[...court], bench:[...bench], stats:{...stats},
    sets:sets.map(s=>({...s,scores:[...s.scores]})),
    currentSet, servingTeam, rallyInSeq, seqCount,
    subAlerts:[...subAlerts], pendingSubSlots:[...pendingSubSlots],
    subHistory:[...subHistory], subPairs:subPairs.map(p=>({...p,triggerSlots:[...p.triggerSlots]})),
    log:[...log]
  });

  const restoreSnapshot = (snap) => {
    setCourt(snap.court); setBench(snap.bench); setStats(snap.stats);
    setSets(snap.sets); setCurrentSet(snap.currentSet);
    setServingTeam(snap.servingTeam); setRallyInSeq(snap.rallyInSeq);
    setSeqCount(snap.seqCount); setSubAlerts(snap.subAlerts);
    setPendingSubSlots(snap.pendingSubSlots); setSubHistory(snap.subHistory);
    setSubPairs(snap.subPairs); setLog(snap.log);
  };

  const undoRally = () => {
    if(history.length===0) return;
    setFuture(f=>[...f, makeSnapshot()]);
    restoreSnapshot(history[history.length-1]);
    setHistory(h=>h.slice(0,-1));
  };

  const redoRally = () => {
    if(future.length===0) return;
    setHistory(h=>[...h, makeSnapshot()]);
    restoreSnapshot(future[future.length-1]);
    setFuture(f=>f.slice(0,-1));
  };

  const recordRally = (weWon) => {
    setHistory(h=>[...h, makeSnapshot()]);
    setFuture([]);

    const newSets = sets.map((s,i)=>i===currentSet?{...s,scores:weWon?[s.scores[0]+1,s.scores[1]]:[s.scores[0],s.scores[1]+1]}:s);
    const newStats = {...stats};
    court.forEach((p,i)=>{
      if(p){
        newStats[p]={...newStats[p],ralliesPlayed:newStats[p].ralliesPlayed+1};
        if(i===0&&rallyInSeq===0) newStats[p]={...newStats[p],timesServed:newStats[p].timesServed+1};
      }
    });

    const nextRally = (rallyInSeq+1)%3;
    const endOfSeq  = rallyInSeq===2;
    let newCourt=[...court], newBench=[...bench], newServingTeam=servingTeam, newSeqCount=seqCount;
    let alerts=[], highlightSlots=[];

    if(endOfSeq){
      newSeqCount = seqCount+1;
      newServingTeam = servingTeam===0?1:0;
      // Only rotate after both teams have completed their sequence (every 2 sequences)
      const shouldRotate = newSeqCount % 2 === 0;
      if(shouldRotate){
        newCourt = rotateCourt(newCourt);
        subPairs.forEach(pair=>{
          const slotIdx = newCourt.indexOf(pair.courtPlayer);
          if(slotIdx===-1) return;
          if(pair.triggerSlots.includes(slotIdx) && newBench.includes(pair.benchPlayer)){
            alerts.push({out:pair.courtPlayer,in:pair.benchPlayer,slot:slotIdx,pairId:pair.id});
            highlightSlots.push(slotIdx);
          }
        });
      }
      addLog(`Seq ${newSeqCount} done. ${weWon?"We":"They"} won. Serve → ${newServingTeam===0?"Us":"Them"}${shouldRotate?" · Rotation!":""}`);
    }

    newCourt.forEach((p,i)=>{ if(p) newStats[p]={...newStats[p],currentPos:SLOT_INFO[i].posType}; });

    const score = newSets[currentSet].scores;
    const setOver = (score[0]>=25||score[1]>=25) && Math.abs(score[0]-score[1])>=2;

    setSets(newSets); setStats(newStats); setCourt(newCourt); setBench(newBench);
    setRallyInSeq(nextRally); setSeqCount(newSeqCount); setServingTeam(newServingTeam);
    setSubAlerts(alerts); setPendingSubSlots(highlightSlots);

    if(setOver){
      const weWonSet = score[0]>score[1];
      setCelebration(weWonSet?'win':'lose');
      setTimeout(()=>setCelebration(null),3500);
      addLog(`Set ${currentSet+1} over! ${score[0]}-${score[1]}`);
      if(currentSet>=2){
        setPhase("summary");
      } else {
        newSets.push({scores:[0,0]});
        setSets(newSets);
        setBetweenSetInfo({setNum:currentSet+1, weWon:weWonSet, nextSet:currentSet+1});
        setPhase("betweenSet");
      }
    }
  };

  const confirmSub = (alert) => {
    setCourt(c=>{ const nc=[...c]; nc[alert.slot]=alert.in; return nc; });
    setBench(b=>{ const nb=b.filter(p=>p!==alert.in); nb.push(alert.out); return nb; });
    setStats(s=>({...s,
      [alert.out]:{...s[alert.out],onCourt:false,currentPos:null},
      [alert.in]: {...s[alert.in], onCourt:true, currentPos:SLOT_INFO[alert.slot].posType}
    }));
    setSubPairs(pairs=>pairs.map(p=>p.id===alert.pairId?{...p,courtPlayer:alert.in,benchPlayer:alert.out}:p));
    setSubHistory(h=>[...h,{set:currentSet+1,seq:seqCount,in:alert.in,out:alert.out,slot:alert.slot}]);
    addLog(`Sub: ${alert.in} IN for ${alert.out} at P${alert.slot+1}`);
    setSubAlerts(a=>a.filter(x=>x.pairId!==alert.pairId));
    setPendingSubSlots(s=>s.filter(x=>x!==alert.slot));
  };

  const skipSub = (alert) => {
    setSubAlerts(a=>a.filter(x=>x.pairId!==alert.pairId));
    setPendingSubSlots(s=>s.filter(x=>x!==alert.slot));
  };

  const resetAll = () => {
    setPhase("setup"); setSetupTab("players"); setStats(initStats());
    setSets([{scores:[0,0]}]); setCurrentSet(0); setRallyInSeq(0); setSeqCount(0);
    setLog([]); setSubHistory([]); setCourt(Array(6).fill(null)); setBench([]);
    setSubAlerts([]); setPendingSubSlots([]); setHistory([]); setFuture([]);
    setBetweenSetInfo(null); setCelebration(null);
  };

  const score = sets[currentSet]?.scores||[0,0];
  const frontSlots=[3,2,1], backSlots=[4,5,0];

  // ---- CELEBRATION ----
  const Celebration = () => {
    if(!celebration) return null;
    const isWin = celebration==='win';
    const items = isWin ? ['🎉','🎊','⭐','🏐','💛','🧡','💚','💙'] : ['😤','👎','🙄'];
    const pieces = Array.from({length:isWin?60:35},(_,i)=>({
      id:i, item:isWin?items[i%items.length]:'Boo',
      left:Math.random()*100, delay:Math.random()*1.5,
      dur:2+Math.random()*1.5, size:isWin?16+Math.random()*14:14+Math.random()*10,
    }));
    return (
      <div style={{position:'fixed',top:0,left:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:9999,overflow:'hidden'}}>
        <style>{`@keyframes fall{0%{transform:translateY(-40px) rotate(0deg);opacity:1}80%{opacity:1}100%{transform:translateY(100vh) rotate(720deg);opacity:0}}`}</style>
        {pieces.map(p=>(
          <div key={p.id} style={{position:'absolute',top:0,left:`${p.left}%`,fontSize:`${p.size}px`,
            fontWeight:isWin?400:700,color:isWin?'inherit':'#dc2626',
            animation:`fall ${p.dur}s ${p.delay}s ease-in forwards`,userSelect:'none'}}>
            {p.item}
          </div>
        ))}
      </div>
    );
  };

  // ---- SHARED COMPONENTS ----
  const PlayerCard = ({name,type,idx,slotIdx,needsSub=false,showStats=false}) => {
    const isCourtSlot = type==="court";
    const info    = isCourtSlot ? SLOT_INFO[slotIdx] : null;
    const posType = isCourtSlot ? info.posType : "Bench";
    const isEmpty = isCourtSlot && !name;
    const myKey   = isCourtSlot ? `court-${slotIdx}` : `bench-${idx}`;
    const isOver  = dragOver===myKey;
    const isDragging = dragActive===myKey;
    return (
      <div
        data-drop-type={type}
        data-drop-idx={isCourtSlot?slotIdx:idx}
        data-drop-key={myKey}
        onPointerDown={name?e=>handlePointerDown(e,type,isCourtSlot?slotIdx:idx):undefined}
        style={{
          border: isDragging ? "2px dashed #3b82f6"
                : isOver     ? "2.5px solid #3b82f6"
                : needsSub   ? "2.5px dashed #ef4444"
                : isEmpty    ? "2px dashed #94a3b8"
                :              `1.5px solid ${POS_COLORS[posType]}`,
          borderRadius:10, padding:"10px 6px", textAlign:"center", minHeight:72,
          cursor: isDragging?"grabbing":name?"grab":"default",
          background: isOver&&!isDragging ? "rgba(59,130,246,0.13)"
                    : isDragging          ? "rgba(59,130,246,0.05)"
                    : isEmpty             ? "transparent"
                    :                       POS_BG[posType],
          opacity: isDragging?0.5:1,
          boxShadow: isOver&&!isDragging ? "0 0 0 3px rgba(59,130,246,0.25)" : "none",
          transition:"background .12s,border .12s,opacity .12s,box-shadow .12s",
          position:"relative", touchAction:"none"}}>
        <div style={{fontSize:10,fontWeight:500,color:isEmpty?"#94a3b8":POS_TEXT[posType],marginBottom:3}}>
          {isCourtSlot ? info.short : "Bench"}
        </div>
        {name ? <>
          <div style={{fontSize:14,fontWeight:500,color:isCourtSlot?POS_TEXT[posType]:POS_TEXT["Bench"]}}>{name}</div>
          {JERSEY[name]!==undefined && <div style={{fontSize:10,color:isCourtSlot?POS_TEXT[posType]:POS_TEXT["Bench"],opacity:.6}}>#{JERSEY[name]}</div>}
          {showStats && <div style={{fontSize:10,color:isCourtSlot?POS_TEXT[posType]:POS_TEXT["Bench"],opacity:.7,marginTop:1}}>{stats[name]?.ralliesPlayed}r · {stats[name]?.timesServed}s</div>}
        </> : <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>{isCourtSlot?info.label:"empty"}</div>}
        {needsSub && <div style={{position:"absolute",top:-8,right:-6,background:"#ef4444",color:"#fff",fontSize:9,borderRadius:10,padding:"1px 6px",fontWeight:500}}>SUB</div>}
      </div>
    );
  };

  const CourtLayout = ({showStats=false}) => (
    <div style={{background:"var(--color-background-secondary)",borderRadius:12,padding:"12px",marginBottom:"1rem"}}>
      <div style={{fontSize:11,color:"var(--color-text-secondary)",textAlign:"center",marginBottom:6}}>← Opponent side →</div>
      <div style={{display:"flex",alignItems:"center",gap:8,margin:"6px 0 10px"}}>
        <div style={{flex:1,borderTop:"3px solid #1e40af"}}/>
        <span style={{fontSize:11,fontWeight:500,color:"#fff",background:"#1e40af",padding:"3px 12px",borderRadius:20,letterSpacing:1}}>NET</span>
        <div style={{flex:1,borderTop:"3px solid #1e40af"}}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
        {frontSlots.map(i=><PlayerCard key={i} name={court[i]} type="court" slotIdx={i} needsSub={pendingSubSlots.includes(i)} showStats={showStats}/>)}
      </div>
      <div style={{borderTop:"1px dashed #94a3b8",margin:"4px 0 8px",opacity:.5}}/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
        {backSlots.map(i=><PlayerCard key={i} name={court[i]} type="court" slotIdx={i} needsSub={pendingSubSlots.includes(i)} showStats={showStats}/>)}
      </div>
      <div style={{fontSize:11,color:"var(--color-text-secondary)",textAlign:"center",marginTop:8}}>← Our side →</div>
    </div>
  );

  const BenchArea = ({showStats=false}) => {
    const isOver = dragOver==="benchArea";
    return (
      <div>
        <div style={{fontSize:12,fontWeight:500,color:"var(--color-text-secondary)",marginBottom:8}}>Bench — drag to swap</div>
        <div data-drop-type="benchDrop" data-drop-key="benchArea"
          style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,minHeight:80,padding:10,
            border:`2px dashed ${isOver?"#3b82f6":"#fca5a5"}`,borderRadius:12,
            background:isOver?"rgba(59,130,246,0.05)":"#fff5f5",transition:"border .15s,background .15s"}}>
          {bench.map((p,i)=><PlayerCard key={p} name={p} type="bench" idx={i} showStats={showStats}/>)}
          {bench.length===0 && <div style={{gridColumn:"span 2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fca5a5",fontStyle:"italic"}}>All players on court</div>}
        </div>
      </div>
    );
  };

  // ---- SETUP ----
  if(phase==="setup") return (
    <div style={{padding:"1rem",maxWidth:600,margin:"0 auto",fontFamily:"var(--font-sans)"}}>
      <h2 style={{fontSize:20,fontWeight:500,margin:"0 0 4px",color:"var(--color-text-primary)"}}>Triple Ball Manager</h2>
      <p style={{fontSize:13,color:"var(--color-text-secondary)",margin:"0 0 1rem"}}>Configure players and substitutions</p>
      <div style={{display:"flex",borderBottom:"0.5px solid var(--color-border-tertiary)",marginBottom:"1rem"}}>
        {["players","subs"].map(t=>(
          <button key={t} onClick={()=>setSetupTab(t)}
            style={{flex:1,padding:"10px 0",fontSize:14,border:"none",background:"transparent",cursor:"pointer",
              fontWeight:setupTab===t?500:400,
              borderBottom:setupTab===t?"2.5px solid #3b82f6":"2.5px solid transparent",
              color:setupTab===t?"var(--color-text-primary)":"var(--color-text-secondary)"}}>
            {t==="players"?"Players":"Substitutions"}
          </button>
        ))}
      </div>

      {setupTab==="players" && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:"1.5rem"}}>
          {PLAYERS.map(p=>(
            <div key={p} style={{background:"var(--color-background-secondary)",borderRadius:10,padding:"10px 12px",opacity:sittingOut[p]?.5:1}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontWeight:500,fontSize:15,color:"var(--color-text-primary)"}}>
                  {p}{JERSEY[p]!==undefined&&<span style={{fontSize:11,color:"var(--color-text-secondary)",marginLeft:4}}>#{JERSEY[p]}</span>}
                </div>
                <button onClick={()=>setSittingOut(s=>({...s,[p]:!s[p]}))}
                  style={{fontSize:11,padding:"4px 9px",borderRadius:20,border:"1.5px solid",cursor:"pointer",
                    background:sittingOut[p]?"#fef2f2":"transparent",
                    color:sittingOut[p]?"#991b1b":"var(--color-text-secondary)",
                    borderColor:sittingOut[p]?"#ef4444":"var(--color-border-tertiary)"}}>
                  {sittingOut[p]?"Out":"In"}
                </button>
              </div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {["Server","Front Row","Back Row"].map(pos=>(
                  <button key={pos} onClick={()=>!sittingOut[p]&&toggleElig(p,pos)}
                    style={{fontSize:10,padding:"3px 7px",borderRadius:20,border:"1.5px solid",
                      background:sittingOut[p]?"transparent":eligibility[p][pos]?POS_BG[pos]:"transparent",
                      color:sittingOut[p]?"var(--color-text-tertiary)":eligibility[p][pos]?POS_TEXT[pos]:"var(--color-text-tertiary)",
                      borderColor:sittingOut[p]?"var(--color-border-tertiary)":eligibility[p][pos]?POS_COLORS[pos]:"var(--color-border-tertiary)",
                      cursor:sittingOut[p]?"default":"pointer"}}>
                    {pos}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{marginBottom:"1.5rem"}}>
          <label style={{fontSize:13,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>Max serves per set</label>
          <input type="number" min={1} max={20} value={maxServes} onChange={e=>setMaxServes(+e.target.value)}
            style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"0.5px solid var(--color-border-secondary)",fontSize:16,background:"var(--color-background-primary)",color:"var(--color-text-primary)"}}/>
        </div>
        <button onClick={()=>setSetupTab("subs")} style={{width:"100%",background:"#3b82f6",color:"#fff",border:"none",borderRadius:10,padding:"14px",fontSize:16,fontWeight:500,cursor:"pointer"}}>
          Next: Substitutions →
        </button>
      </>}

      {setupTab==="subs" && <>
        <p style={{fontSize:13,color:"var(--color-text-secondary)",margin:"0 0 1rem"}}>
          Set up player pairs. The court player starts on court, the bench player starts on bench. When the court player rotates to a trigger position, you'll be prompted to substitute.
        </p>
        {subPairs.length>0 && <div style={{marginBottom:"1rem"}}>
          {subPairs.map(pair=>(
            <div key={pair.id} style={{background:"var(--color-background-secondary)",borderRadius:10,padding:"10px 12px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:14,fontWeight:500,color:"var(--color-text-primary)"}}>{pair.courtPlayer}</span>
                  <span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:"#dcfce7",color:"#14532d",border:"0.5px solid #22c55e"}}>court</span>
                  <span style={{fontSize:12,color:"var(--color-text-secondary)"}}>↔</span>
                  <span style={{fontSize:14,fontWeight:500,color:"var(--color-text-primary)"}}>{pair.benchPlayer}</span>
                  <span style={{fontSize:11,padding:"1px 6px",borderRadius:8,background:"#fef2f2",color:"#991b1b",border:"0.5px solid #ef4444"}}>bench</span>
                </div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,color:"var(--color-text-secondary)"}}>Trigger at:</span>
                  {pair.triggerSlots.sort().map(s=>(
                    <span key={s} style={{fontSize:11,padding:"1px 7px",borderRadius:12,background:"#eff6ff",color:"#1e40af",border:"0.5px solid #3b82f6"}}>P{s+1}</span>
                  ))}
                </div>
              </div>
              <button onClick={()=>removeSubPair(pair.id)} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:8,padding:"6px 10px",fontSize:12,cursor:"pointer",color:"#991b1b"}}>Remove</button>
            </div>
          ))}
        </div>}

        <div style={{background:"var(--color-background-secondary)",borderRadius:10,padding:"12px",marginBottom:"1.5rem"}}>
          <div style={{fontSize:13,fontWeight:500,color:"var(--color-text-primary)",marginBottom:10}}>Add substitution pair</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            <div>
              <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>Player 1 — starts on court</label>
              <select value={newPairCourt} onChange={e=>setNewPairCourt(e.target.value)}
                style={{width:"100%",padding:"10px 8px",borderRadius:8,border:"0.5px solid var(--color-border-secondary)",fontSize:14,background:"var(--color-background-primary)",color:"var(--color-text-primary)"}}>
                <option value="">Select...</option>
                {activePlayers.filter(p=>!pairedPlayers.includes(p)&&p!==newPairBench).map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:4}}>Player 2 — starts on bench</label>
              <select value={newPairBench} onChange={e=>setNewPairBench(e.target.value)}
                style={{width:"100%",padding:"10px 8px",borderRadius:8,border:"0.5px solid var(--color-border-secondary)",fontSize:14,background:"var(--color-background-primary)",color:"var(--color-text-primary)"}}>
                <option value="">Select...</option>
                {activePlayers.filter(p=>!pairedPlayers.includes(p)&&p!==newPairCourt).map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:12,color:"var(--color-text-secondary)",display:"block",marginBottom:6}}>Trigger positions</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {SLOT_INFO.map((s,i)=>(
                <button key={i} onClick={()=>toggleNewPairSlot(i)}
                  style={{padding:"6px 12px",borderRadius:20,border:"1.5px solid",fontSize:13,cursor:"pointer",
                    background:newPairSlots.includes(i)?"#eff6ff":"transparent",
                    color:newPairSlots.includes(i)?"#1e40af":"var(--color-text-secondary)",
                    borderColor:newPairSlots.includes(i)?"#3b82f6":"var(--color-border-tertiary)"}}>
                  {s.short}
                </button>
              ))}
            </div>
          </div>
          <button onClick={addSubPair} disabled={!newPairCourt||!newPairBench||newPairSlots.length===0}
            style={{width:"100%",padding:"10px",borderRadius:8,border:"none",fontSize:14,fontWeight:500,cursor:"pointer",
              background:(!newPairCourt||!newPairBench||newPairSlots.length===0)?"var(--color-background-primary)":"#3b82f6",
              color:(!newPairCourt||!newPairBench||newPairSlots.length===0)?"var(--color-text-tertiary)":"#fff"}}>
            Add pair
          </button>
        </div>

        {unpaired.length>0 && <div style={{background:"#fffbeb",border:"0.5px solid #fbbf24",borderRadius:8,padding:"10px 12px",marginBottom:"1.5rem"}}>
          <div style={{fontSize:12,color:"#92400e"}}>No sub pair: <strong style={{fontWeight:500}}>{unpaired.join(", ")}</strong> — these players won't be substituted automatically.</div>
        </div>}

        <button onClick={goToCourt} style={{width:"100%",background:"#22c55e",color:"#fff",border:"none",borderRadius:10,padding:"14px",fontSize:16,fontWeight:500,cursor:"pointer"}}>
          Set Lineup →
        </button>
      </>}
      {buildVersion && <div style={{textAlign:"center",fontSize:10,color:"var(--color-text-secondary)",padding:"16px 0 4px",opacity:.4}}>v{buildVersion.sha} · {buildVersion.date}</div>}
    </div>
  );

  // ---- LINEUP ----
  if(phase==="lineup") return (
    <div style={{padding:"1rem",maxWidth:600,margin:"0 auto",fontFamily:"var(--font-sans)"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:"1rem"}}>
        <button onClick={()=>setPhase("setup")} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:8,padding:"8px 14px",fontSize:14,cursor:"pointer",color:"var(--color-text-secondary)"}}>← Players</button>
        <h2 style={{fontSize:18,fontWeight:500,margin:0,color:"var(--color-text-primary)"}}>Set Lineup</h2>
      </div>
      <p style={{fontSize:13,color:"var(--color-text-secondary)",margin:"0 0 1rem"}}>Drag players between positions and bench. P1 = server (back right).</p>
      <CourtLayout showStats={false}/>
      <div style={{marginBottom:"1.5rem"}}><BenchArea showStats={false}/></div>
      <div style={{marginBottom:"1.5rem"}}>
        <div style={{fontSize:13,fontWeight:500,color:"var(--color-text-primary)",marginBottom:8}}>Who serves first?</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <button onClick={()=>setServingTeam(0)} style={{padding:"12px",borderRadius:10,border:"1.5px solid",fontSize:14,cursor:"pointer",
            background:servingTeam===0?"#eff6ff":"transparent",color:servingTeam===0?"#1e40af":"var(--color-text-secondary)",
            borderColor:servingTeam===0?"#3b82f6":"var(--color-border-tertiary)"}}>We serve first</button>
          <button onClick={()=>setServingTeam(1)} style={{padding:"12px",borderRadius:10,border:"1.5px solid",fontSize:14,cursor:"pointer",
            background:servingTeam===1?"#fef2f2":"transparent",color:servingTeam===1?"#991b1b":"var(--color-text-secondary)",
            borderColor:servingTeam===1?"#ef4444":"var(--color-border-tertiary)"}}>They serve first</button>
        </div>
      </div>
      <button onClick={startGame} style={{width:"100%",background:"#22c55e",color:"#fff",border:"none",borderRadius:10,padding:"14px",fontSize:16,fontWeight:500,cursor:"pointer"}}>
        Start Game ▶
      </button>
      {buildVersion && <div style={{textAlign:"center",fontSize:10,color:"var(--color-text-secondary)",padding:"16px 0 4px",opacity:.4}}>v{buildVersion.sha} · {buildVersion.date}</div>}
    </div>
  );

  // ---- BETWEEN SETS ----
  if(phase==="betweenSet" && betweenSetInfo) {
    const {setNum, weWon, nextSet} = betweenSetInfo;
    return (
      <div style={{padding:"1.5rem",maxWidth:600,margin:"0 auto",fontFamily:"var(--font-sans)"}}>
        <Celebration/>
        <div style={{textAlign:"center",marginBottom:"2rem"}}>
          <div style={{fontSize:15,color:"var(--color-text-secondary)",marginBottom:6}}>Set {setNum} complete</div>
          <div style={{fontSize:26,fontWeight:500,color:weWon?"#14532d":"#991b1b",marginBottom:8}}>
            {weWon?"We won that set! 🎉":"They won that set 😤"}
          </div>
          <div style={{fontSize:14,color:"var(--color-text-secondary)"}}>
            {sets.filter((_,i)=>i<=setNum).map((s,i)=>`Set ${i+1}: ${s.scores[0]}–${s.scores[1]}`).join("  ·  ")}
          </div>
        </div>
        <div style={{fontSize:15,fontWeight:500,color:"var(--color-text-primary)",marginBottom:12,textAlign:"center"}}>
          What would you like to do for Set {nextSet+1}?
        </div>
        <div style={{display:"grid",gap:10}}>
          <button onClick={()=>{ setCurrentSet(nextSet); setRallyInSeq(0); setSeqCount(0); setBetweenSetInfo(null); setPhase("game"); addLog(`Set ${nextSet+1} started.`); }}
            style={{padding:"16px",borderRadius:10,border:"none",background:"#3b82f6",color:"#fff",fontSize:16,fontWeight:500,cursor:"pointer"}}>
            ▶ Start Set {nextSet+1} with same lineup
          </button>
          <button onClick={()=>{ setCurrentSet(nextSet); setRallyInSeq(0); setSeqCount(0); setBetweenSetInfo(null); setPhase("lineup"); }}
            style={{padding:"16px",borderRadius:10,border:"1.5px solid var(--color-border-secondary)",background:"transparent",fontSize:16,fontWeight:500,cursor:"pointer",color:"var(--color-text-primary)"}}>
            ✏️ Set new lineup for Set {nextSet+1}
          </button>
        </div>
      </div>
    );
  }

  // ---- SUMMARY ----
  if(phase==="summary") return (
    <div style={{padding:"1rem",maxWidth:600,margin:"0 auto",fontFamily:"var(--font-sans)"}}>
      <Celebration/>
      <h2 style={{fontSize:20,fontWeight:500,marginBottom:"1rem",color:"var(--color-text-primary)"}}>Game Summary</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:"1.5rem"}}>
        {sets.map((s,i)=>(
          <div key={i} style={{background:"var(--color-background-secondary)",borderRadius:10,padding:"12px",textAlign:"center"}}>
            <div style={{fontSize:11,color:"var(--color-text-secondary)",marginBottom:4}}>Set {i+1}</div>
            <div style={{fontSize:22,fontWeight:500,color:"var(--color-text-primary)"}}>{s.scores[0]}–{s.scores[1]}</div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:"1.5rem"}}>
        {[...PLAYERS].sort((a,b)=>stats[b].ralliesPlayed-stats[a].ralliesPlayed).map(p=>(
          <div key={p} style={{background:"var(--color-background-secondary)",borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:14,fontWeight:500,color:"var(--color-text-primary)",marginBottom:4}}>
              {p}{JERSEY[p]!==undefined&&<span style={{fontSize:11,color:"var(--color-text-secondary)",marginLeft:4}}>#{JERSEY[p]}</span>}
            </div>
            <div style={{background:"var(--color-background-primary)",borderRadius:4,height:6,marginBottom:4,overflow:"hidden"}}>
              <div style={{width:`${(stats[p].ralliesPlayed/Math.max(...PLAYERS.map(x=>stats[x].ralliesPlayed),1))*100}%`,height:"100%",background:"#3b82f6"}}/>
            </div>
            <div style={{fontSize:12,color:"var(--color-text-secondary)"}}>{stats[p].ralliesPlayed} rallies · {stats[p].timesServed} serves</div>
          </div>
        ))}
      </div>
      <button onClick={resetAll} style={{width:"100%",background:"#3b82f6",color:"#fff",border:"none",borderRadius:10,padding:"14px",fontSize:16,fontWeight:500,cursor:"pointer"}}>
        New Game
      </button>
      {buildVersion && <div style={{textAlign:"center",fontSize:10,color:"var(--color-text-secondary)",padding:"16px 0 4px",opacity:.4}}>v{buildVersion.sha} · {buildVersion.date}</div>}
    </div>
  );

  // ---- GAME ----
  return (
    <div style={{padding:"1rem",maxWidth:600,margin:"0 auto",fontFamily:"var(--font-sans)"}}>
      <Celebration/>

      {subAlerts.map(alert=>(
        <div key={alert.pairId} style={{background:"#fef2f2",border:"2px solid #ef4444",borderRadius:10,padding:"12px 14px",marginBottom:"1rem"}}>
          <div style={{fontSize:14,fontWeight:500,color:"#991b1b",marginBottom:6}}>Sub ready — P{alert.slot+1}</div>
          <div style={{fontSize:13,color:"#991b1b",marginBottom:10}}>
            <strong style={{fontWeight:500}}>{alert.in}</strong> in for <strong style={{fontWeight:500}}>{alert.out}</strong>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <button onClick={()=>confirmSub(alert)} style={{padding:"10px",borderRadius:8,border:"none",background:"#22c55e",color:"#fff",fontSize:14,fontWeight:500,cursor:"pointer"}}>Confirm</button>
            <button onClick={()=>skipSub(alert)} style={{padding:"10px",borderRadius:8,border:"1.5px solid #ef4444",background:"transparent",color:"#991b1b",fontSize:14,cursor:"pointer"}}>Skip</button>
          </div>
        </div>
      ))}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button onClick={()=>setPhase("lineup")} style={{background:"transparent",border:"0.5px solid var(--color-border-secondary)",borderRadius:8,padding:"6px 12px",fontSize:13,cursor:"pointer",color:"var(--color-text-secondary)"}}>← Lineup</button>
          <div>
            <div style={{fontSize:12,color:"var(--color-text-secondary)"}}>Set {currentSet+1}</div>
            <div style={{fontSize:26,fontWeight:500,color:"var(--color-text-primary)",lineHeight:1.1}}>{score[0]} – {score[1]}</div>
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:5,alignItems:"flex-end"}}>
          <span style={{fontSize:12,padding:"4px 10px",borderRadius:20,
            background:servingTeam===0?"#eff6ff":"#f9fafb",
            color:servingTeam===0?"#1e40af":"var(--color-text-secondary)",
            border:"0.5px solid",borderColor:servingTeam===0?"#3b82f6":"var(--color-border-tertiary)"}}>
            {servingTeam===0?"We serve":"They serve"}
          </span>
          <span style={{fontSize:12,padding:"4px 10px",borderRadius:20,background:"#fef9c3",color:"#713f12",border:"0.5px solid #eab308"}}>
            {RALLY_NAMES[rallyInSeq]}
          </span>
        </div>
      </div>

      <div style={{display:"flex",borderBottom:"0.5px solid var(--color-border-tertiary)",marginBottom:"1rem"}}>
        {["court","stats","log"].map(t=>(
          <button key={t} onClick={()=>setActiveTab(t)}
            style={{flex:1,padding:"10px 0",fontSize:14,border:"none",background:"transparent",cursor:"pointer",
              fontWeight:activeTab===t?500:400,
              borderBottom:activeTab===t?"2.5px solid #3b82f6":"2.5px solid transparent",
              color:activeTab===t?"var(--color-text-primary)":"var(--color-text-secondary)"}}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {activeTab==="court" && <>
        <CourtLayout showStats={true}/>
        <div style={{marginBottom:"1rem"}}><BenchArea showStats={true}/></div>
        <div style={{background:"var(--color-background-secondary)",borderRadius:12,padding:"1rem"}}>
          <div style={{fontSize:14,fontWeight:500,color:"var(--color-text-primary)",marginBottom:10}}>
            Record rally — <span style={{color:"#ca8a04"}}>{RALLY_NAMES[rallyInSeq]}</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <button onClick={()=>recordRally(true)} style={{padding:"16px",borderRadius:10,border:"none",background:"#22c55e",color:"#fff",fontSize:16,fontWeight:500,cursor:"pointer"}}>✓ We win</button>
            <button onClick={()=>recordRally(false)} style={{padding:"16px",borderRadius:10,border:"none",background:"#ef4444",color:"#fff",fontSize:16,fontWeight:500,cursor:"pointer"}}>✗ They win</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <button onClick={undoRally} disabled={history.length===0}
              style={{padding:"12px",borderRadius:10,border:"1.5px solid var(--color-border-secondary)",background:"transparent",
                fontSize:14,cursor:history.length===0?"not-allowed":"pointer",
                color:history.length===0?"var(--color-text-tertiary)":"var(--color-text-secondary)"}}>
              ↩ Undo
            </button>
            <button onClick={redoRally} disabled={future.length===0}
              style={{padding:"12px",borderRadius:10,border:"1.5px solid var(--color-border-secondary)",background:"transparent",
                fontSize:14,cursor:future.length===0?"not-allowed":"pointer",
                color:future.length===0?"var(--color-text-tertiary)":"var(--color-text-secondary)"}}>
              Redo ↪
            </button>
          </div>
          <div style={{marginTop:8,fontSize:12,color:"var(--color-text-secondary)",textAlign:"center"}}>
            Rally {rallyInSeq+1}/3 · Sequence {seqCount+1}
          </div>
        </div>
      </>}

      {activeTab==="stats" && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:"1rem"}}>
          {PLAYERS.map(p=>{
            const s=stats[p], maxR=Math.max(...PLAYERS.map(x=>stats[x].ralliesPlayed),1);
            return (
              <div key={p} style={{background:"var(--color-background-secondary)",borderRadius:10,padding:"10px 12px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:14,fontWeight:500,color:"var(--color-text-primary)"}}>
                    {p}{JERSEY[p]!==undefined&&<span style={{fontSize:11,color:"var(--color-text-secondary)",marginLeft:4}}>#{JERSEY[p]}</span>}
                  </span>
                  <span style={{fontSize:11,padding:"2px 7px",borderRadius:12,
                    background:s.onCourt?"#dcfce7":"var(--color-background-primary)",
                    color:s.onCourt?"#14532d":"var(--color-text-secondary)",
                    border:`0.5px solid ${s.onCourt?"#22c55e":"var(--color-border-tertiary)"}`}}>
                    {s.onCourt?s.currentPos:"Bench"}
                  </span>
                </div>
                <div style={{background:"var(--color-background-primary)",borderRadius:4,height:6,marginBottom:4,overflow:"hidden"}}>
                  <div style={{width:`${(s.ralliesPlayed/maxR)*100}%`,height:"100%",background:"#3b82f6"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--color-text-secondary)"}}>
                  <span>{s.ralliesPlayed} rallies</span><span>{s.timesServed} serves</span>
                </div>
              </div>
            );
          })}
        </div>
        {subPairs.length>0 && <div style={{marginBottom:"1rem"}}>
          <div style={{fontSize:13,fontWeight:500,color:"var(--color-text-primary)",marginBottom:8}}>Sub pairs</div>
          {subPairs.map(pair=>(
            <div key={pair.id} style={{background:"var(--color-background-secondary)",borderRadius:8,padding:"8px 12px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,color:"var(--color-text-primary)"}}>{pair.courtPlayer} ↔ {pair.benchPlayer}</span>
              <div style={{display:"flex",gap:4}}>{pair.triggerSlots.sort().map(s=><span key={s} style={{fontSize:11,padding:"1px 6px",borderRadius:12,background:"#eff6ff",color:"#1e40af",border:"0.5px solid #3b82f6"}}>P{s+1}</span>)}</div>
            </div>
          ))}
        </div>}
        {subHistory.length>0 && <>
          <div style={{fontSize:13,fontWeight:500,color:"var(--color-text-primary)",marginBottom:8}}>Substitution history</div>
          {subHistory.map((s,i)=>(
            <div key={i} style={{fontSize:13,color:"var(--color-text-secondary)",padding:"6px 0",borderBottom:"0.5px solid var(--color-border-tertiary)"}}>
              Set {s.set}, Seq {s.seq}: <span style={{color:"#14532d"}}>{s.in}</span> in for <span style={{color:"#9a3412"}}>{s.out}</span> at P{s.slot+1}
            </div>
          ))}
        </>}
      </>}

      {activeTab==="log" && <>
        {log.length===0 && <p style={{fontSize:13,color:"var(--color-text-tertiary)"}}>No events yet.</p>}
        {log.map((l,i)=>(
          <div key={i} style={{fontSize:13,color:"var(--color-text-secondary)",padding:"8px 0",borderBottom:"0.5px solid var(--color-border-tertiary)"}}>{l}</div>
        ))}
      </>}
      {buildVersion && <div style={{textAlign:"center",fontSize:10,color:"var(--color-text-secondary)",padding:"16px 0 4px",opacity:.4}}>v{buildVersion.sha} · {buildVersion.date}</div>}
    </div>
  );
}